import { TextSelectDropdown } from "@circle-react-shared/TextSelectDropdown";
import { useVariableNode } from "@circle-react-shared/uikit/TipTap/Extensions/CustomExtensions/Variable/hooks/useVariableNode";
import { useTextSource } from "@circle-react-uikit/InsertVariableButton/useTextSource";
import type { Editor } from "@tiptap/react";
import noop from "lodash/noop";
import type { ReactNode } from "react";
import { useEffect, useRef } from "react";

export interface Variable {
  group: string;
  isNullable?: boolean;
  label: string;
  value: string;
}

const DROPDOWN_TRIGGER_INPUT_TEXT = "{{";

const URL_VARIABLES = [
  "{{community.url}}",
  "{{trigger.event.url}}",
  "{{trigger.event.space.url}}",
  "{{trigger.event.space.space_group.url}}",
  "{{trigger.comment.url}}",
  "{{trigger.comment.post.url}}",
  "{{trigger.comment.post.space.url}}",
  "{{trigger.comment.post.space.space_group.url}}",
  "{{trigger.post.url}}",
  "{{trigger.post.space.url}}",
  "{{trigger.post.space.space_group.url}}",
];

export const InsertVariableButtonWrapper = ({
  editor,
  inputRef,
  setInputChangeHandler = noop,
  variables,
  wrapperClassName = "max-w-sm pt-3",
  optionsClassName,
  enableLinkInsertion = false,
  children,
  shouldRenderPortal = false,
}: {
  editor?: Editor;
  inputRef?: React.RefObject<HTMLInputElement | null>;
  setInputChangeHandler?: (handler: () => void) => void;
  variables: Variable[];
  wrapperClassName?: string;
  optionsClassName?: string;
  enableLinkInsertion?: boolean;
  children: ReactNode;
  shouldRenderPortal?: boolean;
}) => {
  const textSource = useTextSource({ editor, inputRef });
  const { onInsertVariable } = useVariableNode({ editor, variables });
  const textSourceRef = useRef(textSource);
  textSourceRef.current = textSource;
  const isLinkInsertionSupported =
    Boolean(editor) && Boolean(enableLinkInsertion);

  const getLastTwoChars = () => {
    const textSource = textSourceRef.current;
    if (!textSource) {
      return "";
    }

    const textUpToAnchor = textSource.textUpToAnchor();

    if (textUpToAnchor.length >= 2) {
      return textUpToAnchor.slice(-2);
    }

    return textUpToAnchor;
  };

  const handleInsertVariable = (variableName: string) => {
    const shouldSliceVariableName =
      getLastTwoChars() === DROPDOWN_TRIGGER_INPUT_TEXT;
    const textToInsert = shouldSliceVariableName
      ? variableName.slice(2)
      : variableName;

    const isUrlType = URL_VARIABLES.includes(variableName);
    if (isLinkInsertionSupported && isUrlType) {
      textSource?.insertLink(variableName, shouldSliceVariableName);
    } else if (!onInsertVariable(variableName)) {
      textSource?.insertText(textToInsert);
    }
  };

  const insertVariableButtonRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const handleContentChange = () => {
      const shouldShowDmOptions =
        getLastTwoChars() === DROPDOWN_TRIGGER_INPUT_TEXT;
      if (shouldShowDmOptions && insertVariableButtonRef.current) {
        insertVariableButtonRef.current.click();
      }
    };

    if (editor) {
      editor.on("update", handleContentChange);
    } else {
      setInputChangeHandler(handleContentChange);
    }

    return () => {
      if (editor) {
        editor.off("update", handleContentChange);
      } else {
        setInputChangeHandler(noop);
      }
    };
  }, [editor]);

  return (
    <div className={wrapperClassName}>
      <TextSelectDropdown
        options={variables}
        groupBy={(option: any) => option.group}
        onSelect={(value: unknown) => handleInsertVariable(value as string)}
        buttonRef={insertVariableButtonRef}
        optionsClassName={optionsClassName}
        shouldRenderPortal={shouldRenderPortal}
      >
        {children}
      </TextSelectDropdown>
    </div>
  );
};
